import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { db } from "@/modules/shared/lib/db-client";
import { useAuth } from "@/modules/shared/hooks/useAuth";
import { log, reportError } from "@/modules/shared/lib/observability";

const ACTIVE_ORG_KEY = "openhub_active_org_id";

interface OrgMembership {
  organizationId: string;
  organizationName: string;
}

interface OrganizationContextType {
  organizationId: string | null;
  organizationName: string | null;
  organizations: OrgMembership[];
  loading: boolean;
  switchOrganization: (orgId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [organizations, setOrganizations] = useState<OrgMembership[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setOrganizations([]);
      setActiveOrgId(null);
      setLoading(false);
      return;
    }

    const fetchOrgs = async () => {
      try {
        const { data, error } = await db
          .from("organization_members")
          .select("organization_id, organizations(name)")
          .eq("user_id", user.id);

        if (error) {
          log.error("Error fetching organizations", {}, error);
          setLoading(false);
          return;
        }

        const memberships: OrgMembership[] = (data || []).map((row: any) => ({
          organizationId: row.organization_id,
          organizationName: row.organizations?.name ?? "Sem nome",
        }));

        setOrganizations(memberships);

        // Resolve active org: localStorage → first available
        const stored = localStorage.getItem(ACTIVE_ORG_KEY);
        const validStored = memberships.find((m) => m.organizationId === stored);

        if (validStored) {
          setActiveOrgId(validStored.organizationId);
        } else if (memberships.length > 0) {
          const first = memberships[0].organizationId;
          setActiveOrgId(first);
          localStorage.setItem(ACTIVE_ORG_KEY, first);
        }
      } catch (err) {
        reportError(err, "fetch-organizations");
        log.error("Error in fetchOrgs", {}, err);
      }
      setLoading(false);
    };

    fetchOrgs();
  }, [user, authLoading]);

  const switchOrganization = useCallback((orgId: string) => {
    const match = organizations.find((m) => m.organizationId === orgId);
    if (match) {
      setActiveOrgId(orgId);
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    }
  }, [organizations]);

  const activeOrg = organizations.find((m) => m.organizationId === activeOrgId);

  return (
    <OrganizationContext.Provider
      value={{
        organizationId: activeOrgId,
        organizationName: activeOrg?.organizationName ?? null,
        organizations,
        loading,
        switchOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
